import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Phone, Mail, MapPin, Menu, X, Instagram, ChevronDown, ArrowRight, ShieldCheck, LayoutGrid, ShoppingCart,
} from "lucide-react";
import { siteInfo, navItems, certificates } from "../mock";
import { getProducts, fileUrl } from "../api";
import { useCart } from "../context/CartContext";

const Logo = ({ className = "h-16" }) => (
  <img src={siteInfo.logo} alt="Jump en Joy verhuur" className={`${className} w-auto object-contain`} />
);

const WhatsAppIcon = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 32 32" className={className} fill="currentColor" aria-hidden="true">
    <path d="M16.04 4C9.9 4 4.92 8.98 4.92 15.12c0 2.02.54 3.98 1.56 5.71L4 28l7.36-1.93a11.1 11.1 0 0 0 4.68 1.04h.01c6.13 0 11.11-4.98 11.11-11.12C27.16 8.98 22.18 4 16.04 4Zm0 20.36h-.01a9.2 9.2 0 0 1-4.69-1.28l-.34-.2-3.49.92.93-3.4-.22-.35a9.18 9.18 0 0 1-1.41-4.93c0-5.09 4.14-9.23 9.24-9.23 2.47 0 4.78.96 6.53 2.71a9.16 9.16 0 0 1 2.7 6.53c0 5.1-4.14 9.23-9.24 9.23Zm5.07-6.91c-.28-.14-1.64-.81-1.9-.9-.25-.09-.44-.14-.62.14-.18.28-.71.9-.87 1.08-.16.18-.32.2-.6.07-.28-.14-1.17-.43-2.23-1.38-.82-.73-1.38-1.64-1.54-1.92-.16-.28-.02-.43.12-.57.13-.13.28-.32.41-.48.14-.16.18-.28.28-.46.09-.18.05-.35-.02-.48-.07-.14-.62-1.5-.85-2.05-.22-.54-.45-.46-.62-.47l-.53-.01c-.18 0-.48.07-.73.35-.25.28-.96.94-.96 2.3 0 1.35.99 2.66 1.12 2.84.14.18 1.95 2.97 4.72 4.16.66.28 1.17.45 1.57.58.66.21 1.26.18 1.74.11.53-.08 1.64-.67 1.87-1.32.23-.65.23-1.2.16-1.32-.07-.12-.25-.19-.53-.33Z" />
  </svg>
);

const TikTokIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M16.6 5.82A5.94 5.94 0 0 1 15.16 2h-3.06v12.9a2.66 2.66 0 0 1-2.66 2.6 2.66 2.66 0 0 1-2.66-2.66 2.66 2.66 0 0 1 3.5-2.53V9.2a5.8 5.8 0 0 0-.84-.06A5.72 5.72 0 0 0 3.72 14.86a5.72 5.72 0 0 0 11.44 0V8.72a9.08 9.08 0 0 0 5.12 1.57V7.23a5.9 5.9 0 0 1-3.68-1.41Z" />
  </svg>
);

const WhatsAppFab = () => (
  <a
    href={`https://wa.me/${siteInfo.whatsapp}`}
    target="_blank"
    rel="noopener noreferrer"
    className="fixed bottom-5 right-5 z-[60] flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-white w-16 h-16 sm:w-auto sm:h-auto sm:justify-start sm:pl-4 sm:pr-5 sm:py-3 shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all group"
    aria-label="WhatsApp"
  >
    <WhatsAppIcon className="w-8 h-8 sm:w-6 sm:h-6" />
    <span className="hidden sm:block font-bold text-sm">Stel je vraag</span>
  </a>
);

const TopBar = () => (
  <div className="bg-brand-blue text-white text-[13px] font-semibold text-center py-2 px-4">
    {siteInfo.announcement}
  </div>
);

const MegaPanel = ({ items, onClose, compact = false }) => (
  <div className={`absolute left-0 top-full pt-3 z-50 ${compact ? "w-[560px]" : "w-[1060px]"} max-w-[96vw]`}>
    <div className="mega-anim rounded-3xl bg-brand-blue shadow-2xl p-5">
      <div className={`grid ${compact ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-3"} gap-3`}>
        {items.map((c) => {
          if (c.cta) {
            return (
              <Link key={c.label} to={c.to} onClick={onClose} data-testid="mega-cta-assortiment"
                className="flex items-center gap-3 rounded-2xl bg-brand-pink hover:bg-brand-pinkdark px-4 py-4 transition-all duration-200 hover:-translate-y-0.5">
                <span className="w-12 h-12 rounded-xl shrink-0 bg-white/20 flex items-center justify-center">
                  <LayoutGrid className="w-7 h-7 text-white" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5 font-display text-white text-base leading-tight whitespace-nowrap">
                    <span>{c.label}</span> <ArrowRight className="w-4 h-4 shrink-0" />
                  </span>
                  <span className="block text-white/80 text-sm mt-0.5 whitespace-nowrap">{c.desc}</span>
                </span>
              </Link>
            );
          }
          return (
            <Link key={c.to} to={c.to} onClick={onClose}
              className="flex items-center gap-3 rounded-2xl bg-white/10 hover:bg-brand-pink px-4 py-4 transition-all duration-200 hover:-translate-y-0.5">
              <span className="w-12 h-12 rounded-xl overflow-hidden shrink-0 bg-white/20">
                <img src={c.img} alt={c.label} className="w-full h-full object-cover" loading="lazy" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-display text-white text-base leading-tight truncate">{c.label}</span>
                <span className="block text-white/75 text-sm mt-0.5 whitespace-nowrap">{c.desc}</span>
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  </div>
);

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [mega, setMega] = useState(false);
  const [foodMega, setFoodMega] = useState(false);
  const [mobileSub, setMobileSub] = useState(false);
  const [mobileFoodSub, setMobileFoodSub] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [foodItems, setFoodItems] = useState([]);
  const { count } = useCart();
  const megaRef = useRef(null);
  const foodRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    getProducts().then((ps) => {
      const all = ps || [];
      const isFood = (p) => p.has_vullingen || p.category === "Suikerspin & Popcorn";
      const items = all.filter((p) => !isFood(p)).map((p) => ({
        label: p.name, desc: p.category, img: fileUrl(p.image), to: `/onze-luchtkastelen/${p.slug}`,
      }));
      items.push({ label: "Bekijk ons hele assortiment", desc: "Alle springkussens op een rij", to: "/onze-luchtkastelen", cta: true });
      setMenuItems(items);
      const foods = all.filter(isFood).map((p) => ({
        label: p.name, desc: p.tagline || p.category, img: fileUrl(p.image), to: `/onze-luchtkastelen/${p.slug}`,
      }));
      foods.push({ label: "Bekijk alle extra's", desc: "Suikerspin, popcorn en meer", to: "/onze-luchtkastelen?cat=Suikerspin & Popcorn", cta: true });
      setFoodItems(foods);
    }).catch(() => {});
  }, []);

  useEffect(() => { setOpen(false); setMega(false); setFoodMega(false); setMobileSub(false); setMobileFoodSub(false); }, [location]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);
  useEffect(() => {
    const handler = (e) => {
      if (megaRef.current && !megaRef.current.contains(e.target)) setMega(false);
      if (foodRef.current && !foodRef.current.contains(e.target)) setFoodMega(false);
    };
    if (mega || foodMega) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [mega, foodMega]);

  const goAnchor = (hash) => {
    setOpen(false);
    const scroll = () => { const el = document.getElementById(hash); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); };
    if (location.pathname !== "/") { navigate("/"); setTimeout(scroll, 250); } else scroll();
  };

  const renderDesktopItem = (item) => {
    if (item.mega) {
      return (
        <div key={item.label} className="relative" ref={megaRef}>
          <button onClick={() => setMega((v) => !v)} className={`flex items-center gap-1 text-[15px] font-semibold transition-colors hover:text-brand-blue ${mega ? "text-brand-blue" : "text-brand-navy"}`}>
            {item.label}<ChevronDown className={`w-4 h-4 transition-transform ${mega ? "rotate-180" : ""}`} />
          </button>
          {mega && <MegaPanel items={menuItems} onClose={() => setMega(false)} />}
        </div>
      );
    }
    if (item.food) {
      return (
        <div key={item.label} className="relative" ref={foodRef}>
          <button onClick={() => setFoodMega((v) => !v)} data-testid="nav-food-dropdown" className={`flex items-center gap-1 text-[15px] font-semibold transition-colors hover:text-brand-blue ${foodMega ? "text-brand-blue" : "text-brand-navy"}`}>
            {item.label}<ChevronDown className={`w-4 h-4 transition-transform ${foodMega ? "rotate-180" : ""}`} />
          </button>
          {foodMega && <MegaPanel items={foodItems} onClose={() => setFoodMega(false)} compact />}
        </div>
      );
    }
    if (item.hash) {
      return <button key={item.label} onClick={() => goAnchor(item.hash)} className="text-[15px] font-semibold text-brand-navy hover:text-brand-blue transition-colors">{item.label}</button>;
    }
    return <NavLink key={item.to} to={item.to} className={({ isActive }) => `text-[15px] font-semibold transition-colors hover:text-brand-blue ${isActive ? "text-brand-blue" : "text-brand-navy"}`}>{item.label}</NavLink>;
  };

  return (
    <>
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-slate-100">
      <div className="wrap h-[84px] flex items-center justify-between gap-4">
        <Link to="/" className="shrink-0 transition-transform hover:scale-105"><Logo /></Link>
        <nav className="hidden lg:flex items-center gap-7">{navItems.map(renderDesktopItem)}</nav>
        <div className="flex items-center gap-2.5">
          <a href={`tel:${siteInfo.phoneRaw}`} className="hidden md:inline-flex items-center gap-2 rounded-full border-2 border-brand-blue text-brand-blue font-bold text-sm px-4 py-2.5 hover:bg-brand-bluelight transition-colors">
            <Phone className="w-4 h-4" /> {siteInfo.phone}
          </a>
          <Link to="/winkelwagen" data-testid="nav-cart-link" aria-label="Winkelwagen" className="relative inline-flex items-center justify-center w-11 h-11 rounded-full border-2 border-brand-blue text-brand-blue hover:bg-brand-bluelight transition-colors">
            <ShoppingCart className="w-5 h-5" />
            {count > 0 && <span data-testid="nav-cart-count" className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 rounded-full bg-brand-pink text-white text-[11px] font-bold flex items-center justify-center">{count}</span>}
          </Link>
          <Link to="/contact" className="hidden sm:inline-flex items-center rounded-full bg-brand-pink hover:bg-brand-pinkdark text-white font-bold text-sm px-6 py-2.5 transition-all hover:-translate-y-0.5 shadow-md">
            Reserveer nu
          </Link>
          <button onClick={() => setOpen(true)} className="lg:hidden w-11 h-11 rounded-full bg-brand-blue hover:bg-brand-bluedark text-white flex items-center justify-center shadow-md hover:-translate-y-0.5 transition-all" aria-label="Menu openen"><Menu className="w-6 h-6" /></button>
        </div>
      </div>
    </header>

    {/* Fullscreen mobile menu */}
    {open && (
      <div className="lg:hidden fixed inset-0 z-[70] bg-brand-blue text-white flex flex-col">
        <div className="bg-brand-blue border-b border-white/15">
          <div className="wrap h-[84px] flex items-center justify-between">
            <Link to="/" onClick={() => setOpen(false)}><div className="bg-white rounded-xl p-1.5 shadow-sm inline-flex"><Logo className="h-14" /></div></Link>
            <button onClick={() => setOpen(false)} className="w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors" aria-label="Sluiten"><X className="w-6 h-6" /></button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto wrap py-6 flex flex-col gap-3">
          {navItems.map((item, i) => {
            const style = { animationDelay: `${i * 80}ms` };
            const base = "nav-slide rounded-2xl px-5 py-4 font-display text-2xl transition-colors";
            if (item.mega) {
              return (
                <div key={item.label} style={style} className="nav-slide">
                  <button onClick={() => setMobileSub((v) => !v)} className={`w-full flex items-center justify-between rounded-2xl px-5 py-4 font-display text-2xl transition-colors ${mobileSub ? "bg-brand-pink" : "bg-white/10"}`}>
                    {item.label} <ChevronDown className={`w-6 h-6 transition-transform ${mobileSub ? "rotate-180" : ""}`} />
                  </button>
                  {mobileSub && (
                    <div className="mt-2 flex flex-col gap-2 mega-anim">
                      {menuItems.map((c) => {
                        if (c.cta) {
                          return (
                            <Link key={c.label} to={c.to} onClick={() => setOpen(false)} className="flex items-center gap-3 rounded-xl bg-brand-pink hover:bg-brand-pinkdark px-3 py-2.5 transition-colors">
                              <span className="w-11 h-11 rounded-lg shrink-0 bg-white/20 flex items-center justify-center"><LayoutGrid className="w-6 h-6" /></span>
                              <span className="flex items-center gap-1.5 font-display text-lg">{c.label} <ArrowRight className="w-4 h-4" /></span>
                            </Link>
                          );
                        }
                        return (
                          <Link key={c.to} to={c.to} onClick={() => setOpen(false)} className="flex items-center gap-3 rounded-xl bg-white/10 hover:bg-brand-pink px-3 py-2.5 transition-colors">
                            <span className="w-11 h-11 rounded-lg overflow-hidden shrink-0 bg-white/20"><img src={c.img} alt={c.label} className="w-full h-full object-cover" /></span>
                            <span className="font-display text-lg">{c.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            if (item.food) {
              return (
                <div key={item.label} style={style} className="nav-slide">
                  <button onClick={() => setMobileFoodSub((v) => !v)} className={`w-full flex items-center justify-between rounded-2xl px-5 py-4 font-display text-2xl transition-colors ${mobileFoodSub ? "bg-brand-pink" : "bg-white/10"}`}>
                    {item.label} <ChevronDown className={`w-6 h-6 transition-transform ${mobileFoodSub ? "rotate-180" : ""}`} />
                  </button>
                  {mobileFoodSub && (
                    <div className="mt-2 flex flex-col gap-2 mega-anim">
                      {foodItems.map((c) => {
                        if (c.cta) {
                          return (
                            <Link key={c.label} to={c.to} onClick={() => setOpen(false)} className="flex items-center gap-3 rounded-xl bg-brand-pink hover:bg-brand-pinkdark px-3 py-2.5 transition-colors">
                              <span className="w-11 h-11 rounded-lg shrink-0 bg-white/20 flex items-center justify-center"><LayoutGrid className="w-6 h-6" /></span>
                              <span className="flex items-center gap-1.5 font-display text-lg">{c.label} <ArrowRight className="w-4 h-4" /></span>
                            </Link>
                          );
                        }
                        return (
                          <Link key={c.to} to={c.to} onClick={() => setOpen(false)} className="flex items-center gap-3 rounded-xl bg-white/10 hover:bg-brand-pink px-3 py-2.5 transition-colors">
                            <span className="w-11 h-11 rounded-lg overflow-hidden shrink-0 bg-white/20"><img src={c.img} alt={c.label} className="w-full h-full object-contain p-1" /></span>
                            <span className="font-display text-lg">{c.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            return item.hash ? (
              <button key={item.label} style={style} onClick={() => goAnchor(item.hash)} className={base + " bg-white/10 hover:bg-brand-pink flex items-center justify-between text-left w-full"}>
                {item.label} <ArrowRight className="w-5 h-5 opacity-70" />
              </button>
            ) : (
              <Link key={item.label} style={style} to={item.to} onClick={() => setOpen(false)} className={base + " bg-white/10 hover:bg-brand-pink flex items-center justify-between"}>
                {item.label} <ArrowRight className="w-5 h-5 opacity-70" />
              </Link>
            );
          })}

          <div className="nav-slide mt-4 grid grid-cols-2 gap-3" style={{ animationDelay: "440ms" }}>
            <a href={`tel:${siteInfo.phoneRaw}`} className="flex items-center justify-center gap-2 rounded-full bg-white/15 hover:bg-white/25 font-bold py-4 transition-colors">
              <Phone className="w-5 h-5" /> Bel ons
            </a>
            <a href={`https://wa.me/${siteInfo.whatsapp}`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1ebe5b] font-bold py-4 transition-colors">
              <WhatsAppIcon className="w-5 h-5" /> WhatsApp
            </a>
          </div>
          <Link to="/contact" onClick={() => setOpen(false)} className="nav-slide text-center rounded-full bg-white text-brand-blue font-bold px-6 py-4 text-xl mt-1" style={{ animationDelay: "520ms" }}>
            Reserveer nu
          </Link>
        </div>
      </div>
    )}
    </>
  );
};

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const goAnchor = (hash) => {
    const scroll = () => { const el = document.getElementById(hash); if (el) el.scrollIntoView({ behavior: "smooth" }); };
    if (location.pathname !== "/") { navigate("/"); setTimeout(scroll, 250); } else scroll();
  };
  return (
    <footer className="bg-brand-blue text-white">
      <div className="wrap py-14">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <div className="bg-white inline-flex rounded-2xl p-3"><Logo className="h-20" /></div>
            <p className="text-blue-50/90 text-sm mt-5 max-w-xs leading-relaxed">
              Springkussens, glijbanen en stormbanen voor elk feest. Wij leveren, bouwen op en halen weer op.
            </p>
            <div className="flex gap-3 mt-5">
              <a href={siteInfo.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram-link" aria-label="Instagram" className="w-10 h-10 rounded-full bg-white/15 hover:bg-brand-pink flex items-center justify-center transition-colors"><Instagram className="w-5 h-5" /></a>
              <a href={siteInfo.tiktok} target="_blank" rel="noopener noreferrer" data-testid="footer-tiktok-link" aria-label="TikTok" className="w-10 h-10 rounded-full bg-white/15 hover:bg-brand-pink flex items-center justify-center transition-colors"><TikTokIcon className="w-5 h-5" /></a>
            </div>
          </div>
          <div>
            <h4 className="font-display text-lg mb-4">Menu</h4>
            <ul className="space-y-3">
              {navItems.map((item) => (
                <li key={item.label}>
                  {item.hash ? (
                    <button onClick={() => goAnchor(item.hash)} className="text-blue-50/90 hover:text-white text-sm transition-colors">{item.label}</button>
                  ) : (
                    <Link to={item.to} className="text-blue-50/90 hover:text-white text-sm transition-colors">{item.label}</Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-display text-lg mb-4">Contact</h4>
            <ul className="space-y-3 text-sm text-blue-50/90">
              <li className="flex items-center gap-3"><Phone className="w-4 h-4" /> {siteInfo.phone}</li>
              <li className="flex items-center gap-3"><Mail className="w-4 h-4" /> {siteInfo.email}</li>
              <li className="flex items-center gap-3"><MapPin className="w-4 h-4" /> {siteInfo.region}</li>
            </ul>
            <a href={`https://wa.me/${siteInfo.whatsapp}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-4 rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-white font-bold text-sm px-5 py-2.5 transition-colors">
              <WhatsAppIcon className="w-5 h-5" /> WhatsApp ons
            </a>
          </div>
        </div>

        {/* Certificates */}
        <div className="mt-12 pt-8 border-t border-white/15">
          <p className="font-display text-sm uppercase tracking-wide text-blue-50/80 mb-4">Veilig & gecertificeerd</p>
          <div className="flex flex-wrap gap-3">
            {certificates.map((c) => (
              <span key={c.name} className="inline-flex items-center gap-2 rounded-xl bg-white/12 px-4 py-2.5 text-sm font-semibold">
                <ShieldCheck className="w-4 h-4 text-white" /> {c.name}
              </span>
            ))}
          </div>
        </div>

        <div className="border-t border-white/15 mt-10 pt-6 flex flex-col sm:flex-row justify-between items-center gap-2 text-xs text-blue-50/70">
          <span>© 2026 Jump en Joy. Alle rechten voorbehouden.</span>
          <span>
            made by{" "}
            <a href="https://www.noirdigital.nl" target="_blank" rel="noopener noreferrer" data-testid="noir-digital-link" className="font-bold text-white hover:underline">Noir Digital</a>
          </span>
        </div>
      </div>
    </footer>
  );
};

const Layout = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => { if (!hash) window.scrollTo(0, 0); }, [pathname, hash]);
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <TopBar />
      <Navbar />
      <main className="flex-1"><Outlet /></main>
      <Footer />
      <WhatsAppFab />
    </div>
  );
};

export default Layout;
